import AddOnOptionSelects from './AddOnOptionSelects';
import { splitZamst, ZAMST_HEADING } from '@/utils/addonOptions';
import { BlockStack, Checkbox, InlineStack, Text, TextField } from '@shopify/polaris';

export interface AddOnProductOption {
  shopifyProductId: string;
  title: string;
  vendor?: string | null;
  options?: Array<{ name: string; values: string[] }>;
  variants: Array<{
    selectedOptions?: Array<{ name: string; value: string }>;
    id: string;
    title: string;
    sku: string;
    price: string | null;
    wholesalePriceCents: number | null;
  }>;
}

export interface AddOnSelection {
  productId: string;
  name: string;
  sku: string;
  variantId: string;
  quantity: number;
  /** dollars, resolved for the current audience */
  unitPrice: number;
}

interface AddOnPickerProps {
  addons: AddOnProductOption[];
  selections: AddOnSelection[];
  onChange: (selections: AddOnSelection[]) => void;
  /** 'retail' uses variant price; 'wholesale' uses the wholesale metafield */
  rateType: 'retail' | 'wholesale';
}

function priceFor(v: AddOnProductOption['variants'][number], rateType: 'retail' | 'wholesale') {
  if (rateType === 'wholesale' && v.wholesalePriceCents != null) {
    return v.wholesalePriceCents / 100;
  }
  return v.price != null ? parseFloat(v.price) : 0;
}

/**
 * Add-on selector shared by Quick Order and admin create-for-clinic: check an
 * add-on to include it, pick a variant when there's more than one, set a
 * quantity. Prices follow the order's retail/wholesale audience.
 */
export default function AddOnPicker({ addons, selections, onChange, rateType }: AddOnPickerProps) {
  if (addons.length === 0) return null;

  const bySku = new Map(selections.map((s) => [s.productId, s]));

  const toggle = (product: AddOnProductOption, checked: boolean) => {
    if (!checked) {
      onChange(selections.filter((s) => s.productId !== product.shopifyProductId));
      return;
    }
    const variant = product.variants.find((v) => v.sku) ?? product.variants[0];
    if (!variant) return;
    onChange([
      ...selections,
      {
        productId: product.shopifyProductId,
        name:
          variant.title && variant.title !== 'Default Title'
            ? `${product.title} — ${variant.title}`
            : product.title,
        sku: variant.sku,
        variantId: variant.id,
        quantity: 1,
        unitPrice: priceFor(variant, rateType),
      },
    ]);
  };

  const update = (productId: string, patch: Partial<AddOnSelection>) => {
    onChange(selections.map((s) => (s.productId === productId ? { ...s, ...patch } : s)));
  };

  const renderProduct = (product: AddOnProductOption) => {
    const sel = bySku.get(product.shopifyProductId);
    const variants = product.variants.filter((v) => v.sku);
    return (
      <BlockStack key={product.shopifyProductId} gap="200">
        <Checkbox
          label={product.title}
          checked={!!sel}
          onChange={(checked) => toggle(product, checked)}
        />
        {sel && (
          <BlockStack gap="200">
            <AddOnOptionSelects
              options={product.options}
              variants={variants}
              value={variants.find((v) => v.id === sel.variantId)?.id ?? sel.variantId}
              onChange={(variantId) => {
                const v = variants.find((x) => x.id === variantId);
                if (!v) return;
                update(product.shopifyProductId, {
                  sku: v.sku,
                  variantId: v.id,
                  name: `${product.title} — ${v.title}`,
                  unitPrice: priceFor(v, rateType),
                });
              }}
            />
            <InlineStack gap="300" blockAlign="end" wrap>
              <div style={{ maxWidth: 90 }}>
                <TextField
                  label="Qty"
                  type="number"
                  min={1}
                  value={String(sel.quantity)}
                  onChange={(v) =>
                    update(product.shopifyProductId, {
                      quantity: Math.max(1, parseInt(v, 10) || 1),
                    })
                  }
                  autoComplete="off"
                />
              </div>
              <Text as="p" tone="subdued">
                ${(sel.unitPrice * sel.quantity).toFixed(2)}
              </Text>
            </InlineStack>
          </BlockStack>
        )}
      </BlockStack>
    );
  };

  const { regular, zamst } = splitZamst(addons, (a) => a.title);

  return (
    <BlockStack gap="300">
      {regular.map(renderProduct)}
      {zamst.length > 0 && (
        <BlockStack gap="300">
          <Text as="h3" variant="headingSm">
            {ZAMST_HEADING}
          </Text>
          {zamst.map(renderProduct)}
        </BlockStack>
      )}
    </BlockStack>
  );
}
